// =============================================
// authGuard — central handling for an expired/invalid session.
// =============================================
// When an authenticated API call comes back 401, the app must NOT silently fall
// back to stale localStorage (that's what made an expired session look like
// "data loss"). Instead we clear the dead session and bounce the user to /auth
// with a flag so the login screen can explain what happened.
// =============================================

let handled = false;

/** Called when the API reports the session is invalid/expired (HTTP 401). */
export function handleSessionExpired(): void {
  if (handled) return;          // ignore the stampede of 401s from parallel loads
  const onAuthPage = typeof window !== 'undefined' && /\/auth\b/.test(window.location.pathname);
  // Only latch when we're about to reload into /auth (the reload resets it). Already on
  // /auth there is no reload, so latching would swallow the NEXT session's expiry.
  if (!onAuthPage) handled = true;
  try {
    localStorage.removeItem('akt_auth_token');
    localStorage.removeItem('akt_refresh_token');
    localStorage.removeItem('akt_session_user');
    document.cookie = 'akt_session=; path=/; max-age=0';
    sessionStorage.setItem('akt_session_expired', '1');
  } catch { /* storage may be unavailable */ }
  if (typeof window !== 'undefined' && !onAuthPage) {
    window.location.assign('/auth');
  }
}

// =============================================
// Proactive session check
// =============================================
// The server expires an idle session, but the client only found out when some API
// call happened to return 401 — and many screens render from the local cache first,
// so a user returning after the idle window could browse a few (stale) pages before
// being bounced. verifySession() asks the server directly; callers run it on app
// load, on returning to the tab, on route change and on the first interaction after
// a quiet spell, so an expired session goes to /auth immediately.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let lastVerified = 0;
let verifying: Promise<boolean> | null = null;

/**
 * Check the session against the server unless it was confirmed within `maxAgeMs`.
 * Resolves false (after bouncing to /auth) only on a definite 401. No token (local /
 * offline mode) or an unreachable server resolves true — offline use keeps working.
 */
export function verifySession(maxAgeMs = 30 * 1000): Promise<boolean> {
  if (handled) return Promise.resolve(false);
  if (Date.now() - lastVerified < maxAgeMs) return Promise.resolve(true);
  if (verifying) return verifying;

  let token: string | null = null;
  try { token = localStorage.getItem('akt_auth_token'); } catch { /* storage unavailable */ }
  if (!token) return Promise.resolve(true);

  verifying = fetch(`${API_BASE}/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      if (res.status === 401) { handleSessionExpired(); return false; }
      if (res.ok) lastVerified = Date.now();
      return true;
    })
    .catch(() => true)            // network error — not an auth failure
    .finally(() => { verifying = null; });
  return verifying;
}

/** Did we just bounce here because the session expired? (one-shot read) */
export function consumeSessionExpiredFlag(): boolean {
  try {
    const v = sessionStorage.getItem('akt_session_expired');
    if (v === '1') { sessionStorage.removeItem('akt_session_expired'); return true; }
  } catch { /* ignore */ }
  return false;
}
